import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "@/data/brand";

function isHashLink(href) { return href.startsWith("/#") || href.startsWith("#"); }

function NavLink({ link, onClick }) {
  const linkClassName = `text-[15px] transition-colors ${
    link.highlight ? "font-semibold text-[#F57F17] hover:text-[#D86B0F]" : "text-[#0D1B2A]/85 hover:text-[#0D1B2A]"
  }`;
  if (isHashLink(link.href)) {
    return (
      <a href={link.href} onClick={onClick} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`} className={linkClassName}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.href} onClick={onClick} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`} className={linkClassName}>
      {link.label}
    </Link>
  );
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 inset-x-0 z-50 bg-white ${scrolled ? "border-b border-[#E5E7EA] shadow-[0_2px_24px_rgba(13,27,42,0.04)]" : "border-b border-transparent"}`}
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-wordmark" className="group flex items-baseline">
          <span className="font-serif-d text-[#0D1B2A] text-[24px] md:text-[28px] tracking-[-0.01em] font-medium">Life</span>
          <span className="font-serif-d text-[#F57F17] text-[24px] md:text-[28px] tracking-[-0.01em] font-semibold ml-[1px]">IQ</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((l) => <NavLink key={l.href} link={l} />)}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/#contact"
            data-testid="nav-book-pilot"
            className="hidden md:inline-flex items-center justify-center bg-[#F57F17] text-white rounded-sm px-4 py-2.5 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition"
          >
            Book a Pilot
          </a>
          <button
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2 rounded-md border border-[#E5E7EA] text-[#0D1B2A]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu-panel" className="xl:hidden border-t border-[#E5E7EA] bg-white">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((l) => <NavLink key={l.href} link={l} onClick={() => setOpen(false)} />)}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-pilot"
              className="mt-2 inline-flex items-center justify-center bg-[#F57F17] text-white rounded-sm px-4 py-3 text-[13px] font-semibold tracking-wide uppercase"
            >
              Book a Pilot
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
